import { useEffect, useRef, useState } from "react";
import type { InterruptionReason, VideoPlaybackMeta } from "@gazelab/shared";
import type { PresentedFrame } from "@gazelab/analysis";
import { afterPaint, measureFromRect, measureImageBounds } from "../../../features/experiment/geometry.js";
import { createPracticeImage } from "../../../features/experiment/preload.js";
import { Actions, Panel } from "../ui.js";

/** Fondo gris neutro uniforme compartido por cruz y estímulos. */
export const NEUTRAL_BG = "#808080";

/** Cruz de fijación central. No se registra mirada durante este periodo. */
export function FixationCross({ durationMs, onDone }: { durationMs: number; onDone: () => void }) {
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const t = setTimeout(() => done.current(), durationMs);
    return () => clearTimeout(t);
  }, [durationMs]);
  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ background: NEUTRAL_BG, cursor: "none" }} data-testid="fixation-cross">
      <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden>
        <path d="M24 4v40M4 24h40" stroke="#1f1f1f" strokeWidth="4" />
      </svg>
    </div>
  );
}

export interface StimulusHandle {
  /** Interrumpe la presentación en curso (deja de esperar el temporizador / detiene el video). */
  cancel: () => void;
  /** Solo video: fotogramas presentados y datos de reproducción hasta este momento. */
  videoSnapshot?: () => VideoCapture;
}

/** Registro de reproducción de un ensayo de video. */
export interface VideoCapture {
  frames: PresentedFrame[];
  meta: VideoPlaybackMeta;
}

/** Una detención de la reproducción más larga que esto interrumpe el ensayo. */
const MAX_STALL_MS = 2000;

/**
 * Muestra una fotografía decodificada a pantalla completa, sin ningún otro elemento.
 * Secuencia: decode() → visible → doble rAF (pintado) → medir geometría → `onVisible` (inicia
 * captura) → esperar `durationMs` con performance.now() → `onElapsed`.
 */
export function StimulusImage({
  url,
  durationMs,
  onVisible,
  onElapsed,
  registerHandle,
}: {
  url: string;
  durationMs: number;
  onVisible: (img: HTMLImageElement, bounds: ReturnType<typeof measureImageBounds>) => Promise<number>;
  onElapsed: (img: HTMLImageElement) => void;
  registerHandle?: (h: StimulusHandle | null) => void;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [visible, setVisible] = useState(false);
  const cbs = useRef({ onVisible, onElapsed, registerHandle });
  cbs.current = { onVisible, onElapsed, registerHandle };

  useEffect(() => {
    let cancelled = false;
    let raf = 0;
    cbs.current.registerHandle?.({
      cancel: () => {
        cancelled = true;
        cancelAnimationFrame(raf);
      },
    });
    (async () => {
      const img = imgRef.current;
      if (!img) return;
      await img.decode();
      if (cancelled) return;
      setVisible(true);
      await afterPaint();
      if (cancelled) return;
      const bounds = measureImageBounds(img);
      const t0 = await cbs.current.onVisible(img, bounds);
      if (cancelled) return;
      const tick = () => {
        if (cancelled) return;
        if (performance.now() - t0 >= durationMs) cbs.current.onElapsed(img);
        else raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    })().catch(() => {
      /* Errores de decodificación: la imagen ya fue decodificada en la precarga; se ignoran aquí. */
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      cbs.current.registerHandle?.(null);
    };
  }, [url, durationMs]);

  return (
    <div className="fixed inset-0" style={{ background: NEUTRAL_BG, cursor: "none" }} data-testid="stimulus-screen">
      <img
        ref={imgRef}
        src={url}
        alt=""
        draggable={false}
        data-testid="stimulus-image"
        style={{
          position: "absolute",
          inset: 0,
          width: "100vw",
          height: "100vh",
          objectFit: "contain",
          objectPosition: "center",
          visibility: visible ? "visible" : "hidden",
          userSelect: "none",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

/**
 * Reproduce un video precargado (Blob) a pantalla completa, sin controles, y registra cada
 * fotograma presentado con `requestVideoFrameCallback` (mismo reloj que las muestras de mirada).
 * Secuencia: datos listos → pintado → medir geometría → `onVisible` (inicia captura, devuelve t0)
 * → play() → fotogramas → `ended` → `onEnded`. Un error, un bloqueo de reproducción o una
 * detención prolongada llaman `onFailure` (el ensayo se guarda como interrumpido).
 */
export function StimulusVideo({
  url,
  muted,
  onVisible,
  onEnded,
  onFailure,
  registerHandle,
}: {
  url: string;
  muted: boolean;
  onVisible: (video: HTMLVideoElement, bounds: ReturnType<typeof measureImageBounds>) => Promise<number>;
  onEnded: (video: HTMLVideoElement, capture: VideoCapture) => void;
  onFailure: (reason: InterruptionReason) => void;
  registerHandle?: (h: StimulusHandle | null) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cbs = useRef({ onVisible, onEnded, onFailure, registerHandle });
  cbs.current = { onVisible, onEnded, onFailure, registerHandle };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    let finished = false;
    let t0 = 0;
    let rvfc = 0;
    let stallStart: number | null = null;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const frames: PresentedFrame[] = [];
    let presented = 0;
    let stallCount = 0;
    let stallMs = 0;

    const snapshot = (endedNaturally: boolean): VideoCapture => ({
      frames: [...frames],
      meta: {
        mediaDurationMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0,
        firstFrameOffsetMs: frames.length ? Math.round((frames[0]!.displayTimeMs - t0) * 10) / 10 : null,
        framesPresented: presented,
        framesDropped: video.getVideoPlaybackQuality?.().droppedVideoFrames ?? null,
        stallCount,
        stallMs: Math.round(stallMs + (stallStart === null ? 0 : performance.now() - stallStart)),
        endedNaturally,
      },
    });
    const stop = () => {
      cancelled = true;
      clearTimeout(stallTimer);
      if (rvfc) video.cancelVideoFrameCallback?.(rvfc);
      video.pause();
    };
    const fail = (reason: InterruptionReason) => {
      if (cancelled || finished) return;
      finished = true;
      stop();
      cbs.current.onFailure(reason);
    };
    cbs.current.registerHandle?.({ cancel: stop, videoSnapshot: () => snapshot(false) });

    const onFrame = (_now: number, meta: VideoFrameCallbackMetadata) => {
      if (cancelled) return;
      frames.push({ displayTimeMs: meta.expectedDisplayTime, mediaTimeMs: meta.mediaTime * 1000 });
      presented = meta.presentedFrames;
      rvfc = video.requestVideoFrameCallback(onFrame);
    };
    const onWaiting = () => {
      if (stallStart !== null || finished) return;
      stallStart = performance.now();
      stallCount++;
      stallTimer = setTimeout(() => fail("playback_stalled"), MAX_STALL_MS);
    };
    const onPlaying = () => {
      if (stallStart === null) return;
      stallMs += performance.now() - stallStart;
      stallStart = null;
      clearTimeout(stallTimer);
    };
    const onEndedEvent = () => {
      if (cancelled || finished) return;
      finished = true;
      const capture = snapshot(true);
      stop();
      cbs.current.onEnded(video, capture);
    };
    const onError = () => fail("media_error");
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("ended", onEndedEvent);
    video.addEventListener("error", onError);

    (async () => {
      if (video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
        await new Promise<void>((resolve, reject) => {
          video.addEventListener("canplaythrough", () => resolve(), { once: true });
          video.addEventListener("error", () => reject(new Error("media")), { once: true });
        });
      }
      if (cancelled) return;
      await afterPaint();
      if (cancelled) return;
      const bounds = measureFromRect(video.getBoundingClientRect(), video.videoWidth, video.videoHeight, window);
      t0 = await cbs.current.onVisible(video, bounds);
      if (cancelled) return;
      rvfc = video.requestVideoFrameCallback(onFrame);
      try {
        await video.play();
      } catch (e) {
        fail(e instanceof DOMException && e.name === "NotAllowedError" ? "autoplay_blocked" : "media_error");
      }
    })().catch(() => fail("media_error"));

    return () => {
      stop();
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("ended", onEndedEvent);
      video.removeEventListener("error", onError);
      cbs.current.registerHandle?.(null);
    };
  }, [url]);

  return (
    <div className="fixed inset-0" style={{ background: NEUTRAL_BG, cursor: "none" }} data-testid="stimulus-screen">
      <video
        ref={videoRef}
        src={url}
        muted={muted}
        playsInline
        preload="auto"
        disablePictureInPicture
        disableRemotePlayback
        controls={false}
        data-testid="stimulus-video"
        style={{
          position: "absolute",
          inset: 0,
          width: "100vw",
          height: "100vh",
          objectFit: "contain",
          objectPosition: "center",
          background: NEUTRAL_BG,
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

/** Práctica opcional con una imagen neutra generada localmente. No se captura ni envía nada. */
export function PracticeRunner({ fixationMs, durationMs, onDone }: { fixationMs: number; durationMs: number; onDone: () => void }) {
  const [step, setStep] = useState<"intro" | "fixation" | "image" | "done">("intro");
  const [img, setImg] = useState<{ url: string } | null>(null);

  useEffect(() => {
    let url: string | null = null;
    createPracticeImage()
      .then((p) => {
        url = p.url;
        setImg({ url: p.url });
      })
      .catch(() => setImg(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  if (step === "fixation") return <FixationCross durationMs={fixationMs} onDone={() => setStep("image")} />;
  if (step === "image" && img) {
    return <StimulusImage url={img.url} durationMs={durationMs} onVisible={async () => performance.now()} onElapsed={() => setStep("done")} />;
  }
  return (
    <Panel title={step === "done" ? "Práctica terminada" : "Práctica"}>
      {step === "done" ? (
        <p>Así se verá cada fotografía del estudio. Ahora calibraremos la cámara.</p>
      ) : (
        <p>
          Verás una cruz en el centro y después una imagen de ejemplo. Solo obsérvala. <strong>Estos datos de práctica no se
          guardan.</strong>
        </p>
      )}
      <Actions>
        {step === "done" ? (
          <button type="button" className="btn-primary" onClick={onDone} data-testid="practice-done">
            Ir a la calibración
          </button>
        ) : (
          <>
            <button type="button" className="btn-primary" disabled={!img} onClick={() => setStep("fixation")} data-testid="practice-start">
              Iniciar práctica
            </button>
            <button type="button" className="btn-secondary" onClick={onDone} data-testid="practice-skip">
              Omitir práctica
            </button>
          </>
        )}
      </Actions>
    </Panel>
  );
}
